<template>
  <div class="well">
    <ais-sort-by
      v-show="false"
      :items="sortByItems"/>
    <ais-panel>
      <div class="filter-header-with-tooltip">
        <h2 class="filter-header">Subjects</h2>
        <tooltip
          v-if="hasAgenticResults"
          text="Selecting a subject will replace these search results."
          placement="top">
          <i
            class="material-icons-outlined info-icon"
            aria-label="Selecting a subject will replace these search results."
            tabindex="0">info</i>
        </tooltip>
      </div>
      <ais-menu-select
        attribute="subject"
        :limit="25"
        :transform-items="transformSubjectItems">
        <template v-slot="{ items, refine }">
          <A11yDropdown
            :dropdown-props="{
              ref:'subject-dropdown',
              class: 'browse-assessments-subject-dropdown',
            }"
            :button-props="{
              class: 'btn btn-default dropdown-toggle ellipsis',
              'data-role': 'button',
              'aria-label': selectedSubject ? `Subject: ${selectedSubject}` : 'Select a subject'
            }"
            :dom-attributes="{
              role: 'listbox',
              'aria-label': selectedSubject ? `Subject: ${selectedSubject}` : 'Select a subject'
            }"
          >
            <template slot="button-contents">
              {{ subjectFilterText }} <i class="fa fa-caret-down" />
            </template>
            <template slot="dropdown-items">
              <li
                v-for="subject in items"
                :key="subject.value"
                role="option"
                :class="{'disabled': shouldDisableSubject(subject)}"
                @click="shouldDisableSubject(subject) ? null : onSubjectChanged(subject, refine)">
                <a class="subject-option" :class="{'disabled': shouldDisableSubject(subject)}">{{ subject.label }}</a>
              </li>
            </template>
          </A11yDropdown>
        </template>
      </ais-menu-select>
    </ais-panel>
    <ais-panel>
      <h2 class="filter-header">Grades</h2>
      <ais-refinement-list
        v-if="isSubjectSelected && showGrades"
        attribute="grades"
        :limit="20"
        :class-names="{
          'ais-RefinementList': 'ais-RefinementListGrades'
        }"
        :transform-items="transformGrades"
      >
        <template v-slot:item="{ item, refine }">
          <label
            class="ais-RefinementList-label grades"
            :class="{
              'selected': item.isRefined,
              'disabled': item.count < 1,
              'adult-transition': item.value === 'Adult-Transition',
              'birth-3': item.value === 'Birth-3'
            }">
            <LockableInputWrapper :locked="!isPremiumUser">
              <input
                class="ais-RefinementList-checkbox"
                type="checkbox"
                tabindex="0"
                :value="item.value"
                :checked="item.isRefined"
                :disabled="!isPremiumUser || item.count < 1"
                @change="onGradeChanged(item, refine, $event)"
              >
            </LockableInputWrapper>
            <span class="ais-RefinementList-labelText">{{ item.label }}</span>
          </label>
        </template>
      </ais-refinement-list>
      <div v-if="!isSubjectSelected" class="disabled">Select a subject</div>
      <div v-if="!showGrades && isSubjectSelected" class="disabled">N/A</div>
    </ais-panel>
    <ais-panel>
      <h2 class="filter-header">Categories</h2>
      <ais-menu
        v-if="isSubjectSelected && !loading && categoryOptionsForSelectedSubject.length"
        :attribute=categoryAttribute
        operator="or"
        :limit="25"
        :transform-items="transformCategoryItems">
        <template v-slot="{ items, refine }">
          <ul>
            <li v-for="item in items" :key="item.value">
              <label class="ais-RefinementList-label" :class="{'selected': item.isRefined}">
                <LockableInputWrapper
                  :locked="!isPremiumUser">
                  <input
                    class="ais-RefinementList-radio"
                    type="radio"
                    role="radio"
                    tabindex="0"
                    :value="item.value"
                    :checked="item.isRefined"
                    :disabled="!isPremiumUser"
                    v-if="item.count >= 1"
                    @click="sendFilterEvent('domains', item); refine(item.value)"
                  >
                </LockableInputWrapper>
                <span class="ais-RefinementList-labelText" :class="{'disabled': item.count < 1}">{{ item.label }}</span>
              </label>
            </li>
          </ul>
        </template>
      </ais-menu>
      <div v-else-if="!isSubjectSelected" class="disabled">Select a subject</div>
      <p v-else-if="!categoryOptionsForSelectedSubject.length" class="text-muted">
        <i>No applicable categories</i>
      </p>
    </ais-panel>
    <ais-panel>
      <h2 class="filter-header">Assessment Type</h2>
      <ais-menu
        v-if="isSubjectSelected && !loading"
        attribute="type"
        operator="or"
        :limit="25"
        :transform-items="transformTypeItems">
        <template v-slot="{ items, refine }">
          <ul>
            <li v-for="item in items" :key="item.value">
              <label class="ais-RefinementList-label" :class="{'selected': item.isRefined, 'disabled': item.count < 1}">
                <LockableInputWrapper :locked="false">
                  <input
                    class="ais-RefinementList-radio"
                    type="radio"
                    role="radio"
                    tabindex="0"
                    :value="item.value"
                    :checked="item.isRefined"
                    :disabled="item.count < 1"
                    @click="sendFilterEvent('type', item); refine(item.value)">
                </LockableInputWrapper>
                <span class="ais-RefinementList-labelText" :class="{'disabled': item.count < 1}">{{ item.label }}</span>
              </label>
            </li>
          </ul>
        </template>
      </ais-menu>
      <div v-else-if="!isSubjectSelected" class="disabled">Select a subject</div>
    </ais-panel>
    <ais-panel>
      <h2 class="filter-header">Language</h2>
      <ais-menu-select
        attribute="language"
        :limit="25"
        :transform-items="transformLanguageItems">
        <template v-slot="{ items, refine }">
          <A11yDropdown
            v-if="isSubjectSelected"
            :dropdown-props="{
              ref:'language-dropdown',
              class: 'browse-assessments-language-dropdown',
            }"
            :button-props="{
              class: 'btn btn-default dropdown-toggle ellipsis',
              'data-role': 'button',
              'aria-label': selectedLanguage ? `Language: ${selectedLanguage}` : 'Select a language'
            }"
            :dom-attributes="{
              role: 'listbox',
              'aria-label': selectedLanguage ? `Language: ${selectedLanguage}` : 'Select a language'
            }"
          >
            <template slot="button-contents">
              {{ languageFilterText }} <i class="fa fa-caret-down" />
            </template>
            <template slot="dropdown-items">
              <li
                v-for="language in items"
                :key="language.value"
                role="option"
                :class="{ 'disabled': language.disabled }"
                :aria-disabled="language.disabled ? 'true' : 'false'"
                @click="language.disabled ? null : onLanguageChanged(language, refine)">
                <a class="language-option" :class="{ 'disabled': language.disabled }">
                  {{ language.label }}
                </a>
              </li>
            </template>
          </A11yDropdown>
          <div v-else class="disabled">Select a subject</div>
        </template>
      </ais-menu-select>
    </ais-panel>
    <ais-panel>
      <h2 class="filter-header">Search</h2>
      <ais-search-box :search-client="searchClient" :index-name="algoliaIndex">
        <debounced-search-box
          @updatedQuery="onUpdatedQuery"
          :disabled="!isPremiumUser"
          :ref-name="searchBoxRefName"/>
      </ais-search-box>
    </ais-panel>
  </div>
</template>
<script>
import { AisDynamicWidgets, AisRefinementList, AisPanel, AisMenuSelect, AisStateResults, AisSortBy, AisMenu } from 'vue-instantsearch';
import A11yDropdown from '@/components/A11yDropdown'
import DebouncedSearchBox from '@/components/search/DebouncedSearchBox.vue'
import LockableInputWrapper from '@/components/LockableInputWrapper.vue'
import { mapGetters } from 'vuex';
import { noSelMixin } from '@/contentprofile/no_sel_mixins'
import { AVAILABLE_LANGUAGES, assessmentGradesSet } from './assessments_filter_options'

export default {
  name: 'BrowseAssessmentsFilters',
  components: {
    DebouncedSearchBox,
    AisDynamicWidgets,
    AisMenuSelect,
    AisPanel,
    AisRefinementList,
    AisStateResults,
    AisSortBy,
    A11yDropdown,
    AisMenu,
    LockableInputWrapper
  },
  mixins: [ noSelMixin ],
  props: {
    algoliaIndex: {
      type: String,
      required: true
    },
    showGrades: {
      type: Boolean,
      required: true
    },
    loading: {
      type: Boolean,
      required: true
    },
    searchClient: {
      type: Object,
      required: true
    },
    onUpdatedQuery: {
      type: Function,
      required: true
    },
    selectedSubject: {
      type: String,
      default: null
    },
    keywordQuery: {
      type: String,
      default: ''
    },
    categoryOptions: {
      type: Object,
      required: false,
      default: () => {}
    },
    defaultSubjects: {
      type: Array,
      default: () => []
    },
    searchBoxRefName: {
      type: String,
      required: true
    },
    selectedLanguage: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      subjectCountsByLanguage: {},
      selectedAssessmentType: null,
      gradeCountsBySubject: {}
    }
  },
  watch: {
    async selectedSubject(newSubject, oldSubject) {
      // When subject is removed, reset language to English and clear subject counts
      if (!newSubject && oldSubject) {
        this.$emit('update:selectedLanguage', 'English')
        this.subjectCountsByLanguage = {}
        this.gradeCountsBySubject = {}
      } else if (newSubject && newSubject !== oldSubject) {
        // When subject is selected or changed, fetch grade counts for that subject
        // Check if keyword query exists to determine whether to suppress characteristics
        const suppressCharacteristics = !this.keywordQuery
        const counts = await this.getGradeCountsBySubject(newSubject, suppressCharacteristics)
        if (this.selectedSubject === newSubject) {
          this.gradeCountsBySubject = counts
        }
      }
    },
    async selectedLanguage(newLanguage, oldLanguage) {
      if (newLanguage && newLanguage !== 'English') {
        const counts = await this.getSubjectCountsByLanguage(newLanguage)
        if (this.selectedLanguage === newLanguage) {
          this.subjectCountsByLanguage = counts
        }
      } else {
        this.subjectCountsByLanguage = {}
      }
    },
    async keywordQuery(newQuery, oldQuery) {
      // Only refetch grades when query transitions from empty to non-empty or vice versa
      if (this.selectedSubject) {
        const queryWasEmptyString = !oldQuery
        const queryIsEmptyString = !newQuery

        if (queryWasEmptyString !== queryIsEmptyString) {
          // Suppress characteristics when no keyword (browsing)
          // Don't suppress when keyword search is active
          const suppressCharacteristics = queryIsEmptyString
          const counts = await this.getGradeCountsBySubject(this.selectedSubject, suppressCharacteristics)
          if (this.selectedSubject) {
            this.gradeCountsBySubject = counts
          }
        }
      }
    }
  },
  async mounted() {
    if (this.selectedLanguage && this.selectedLanguage !== 'English') {
      const language = this.selectedLanguage
      const counts = await this.getSubjectCountsByLanguage(language)
      if (this.selectedLanguage === language) {
        this.subjectCountsByLanguage = counts
      }
    }
  },
  methods: {
    sendFilterEvent(filterType, filterItem) {
      let eventAttrs = {}
      if (filterType === 'domains' && filterItem.isRefined) {
        eventAttrs['browse_assessments_unselected_category'] = filterItem.value
        this.$sendEvent('Unselected Browse Assessments Category', eventAttrs)

      } else if (filterType === 'domains' && !filterItem.isRefined) {
        eventAttrs['browse_assessments_selected_category'] = filterItem.value
        this.$sendEvent('Selected Browse Assessments Category', eventAttrs)

      } else if (filterType === 'grades' && filterItem.isRefined) {
        eventAttrs['browse_assessments_unselected_grade'] = filterItem.value
        this.$sendEvent('Unselected Browse Assessments Grade', eventAttrs)

      } else if (filterType === 'type' && filterItem.isRefined) {
        eventAttrs['browse_assessments_unselected_type'] = filterItem.value
        this.$sendEvent('Unselected Browse Assessments Type', eventAttrs)
        this.selectedAssessmentType = null

      } else if (filterType === 'type' && !filterItem.isRefined) {
        eventAttrs['browse_assessments_selected_type'] = filterItem.value
        this.$sendEvent('Selected Browse Assessments Type', eventAttrs)
        this.selectedAssessmentType = filterItem.value

      } else if (filterType === 'grades' && !filterItem.isRefined) {
        eventAttrs['browse_assessments_selected_grade'] = filterItem.value
        this.$sendEvent('Selected Browse Assessments Grade', eventAttrs)
      }
    },
    clearFocus(event, isRefined) {
      if (isRefined) {
        event.target.blur();
      }
    },
    onGradeChanged(item, refine, event) {
      this.sendFilterEvent('grades', item)
      this.clearFocus(event, item.isRefined)
      refine(item.value)
    },

    onSubjectChanged(subject, refine) {
      if (subject.isRefined) {
        // Subject is currently selected, clicking it will deselect it
        const eventAttrs = { 'browse_assessments_removed_subject': subject.value }
        this.$sendEvent('Removed Browse Assessments Subject', eventAttrs)
      } else {
        // Subject is not currently selected, clicking it will select it
        const eventAttrs = { 'browse_assessments_selected_subject': subject.value }
        this.$sendEvent('Selected Browse Assessments Subject', eventAttrs)
      }
      refine(subject.value)
    },
    onLanguageChanged(language, refine) {
      // Clicking the currently selected language deselects it and defaults to English
      const isClickingCurrentLanguage = this.selectedLanguage === language.value
      const newLanguage = isClickingCurrentLanguage ? 'English' : language.value
      // Only send event if the language is actually changing
      if (this.selectedLanguage !== newLanguage) {
        const eventAttrs = { 'browse_assessments_selected_language': newLanguage }
        this.$sendEvent('Selected Browse Assessments Language', eventAttrs)
      }
      refine(language.value)
    },
    shouldDisableSubject(subject) {
      // Only disable subjects when a non-English language is selected and they have no content
      const isNonEnglishLanguageSelected = this.selectedLanguage && this.selectedLanguage !== 'English'
      if (isNonEnglishLanguageSelected) {
        return subject.count < 1
      }
      // Never disable subjects when English is selected or no language filter is active
      return false
    },
    async getSubjectCountsByLanguage(language) {
      if (!language || language === 'English') {
        return {}
      }
      if (!AVAILABLE_LANGUAGES.includes(language)) {
        console.warn(`Invalid language "${language}" not in AVAILABLE_LANGUAGES, skipping filter`)
        return {}
      }
      try {
        const response = await this.searchClient.search([{
          indexName: this.algoliaIndex,
          params: {
            query: '',
            filters: `language:"${language}"`,
            facets: ['subject'],
            hitsPerPage: 0
          }
        }])
        return response.results[0]?.facets?.subject || {}
      } catch (error) {
        console.error('Error fetching subject counts by language:', error)
        return {}
      }
    },
    async getGradeCountsBySubject(subject, suppressCharacteristics = true) {
      if (!subject) {
        return {}
      }
      try {
        const params = {
          query: '',
          facetFilters: [[`subject:${subject}`]],
          facets: ['grades'],
          hitsPerPage: 0
        }

        // Apply BLV/DHH characteristic filters only when suppressCharacteristics is true
        if (suppressCharacteristics) {
          const CHARACTERISTIC_ID_BLV = 'recjUHa6goKzM1jnr'
          const CHARACTERISTIC_ID_DHH = 'recw811h4t0eAEah9'
          params.filters = `NOT characteristics:${CHARACTERISTIC_ID_BLV} AND NOT characteristics:${CHARACTERISTIC_ID_DHH}`
        }

        const response = await this.searchClient.search([{
          indexName: this.algoliaIndex,
          params: params
        }])
        return response.results[0]?.facets?.grades || {}
      } catch (error) {
        console.error('Error fetching grade counts by subject:', error)
        return {}
      }
    },
    transformSubjectItems(items, { results }) {
      let newItems = []
      for (let subjectOption of this.assessmentSubjectOptionsForProfile) {
        const foundItem = items.find((item) => item.value === subjectOption.getValue())
        const useLanguageCounts = this.selectedLanguage && this.selectedLanguage !== 'English' && Object.keys(this.subjectCountsByLanguage).length > 0
        const count = useLanguageCounts
          ? (this.subjectCountsByLanguage[subjectOption.getValue()] || 0)
          : (foundItem ? foundItem.count : 0)

        newItems.push({
          label: subjectOption.getLabel(),
          count: count,
          isRefined: foundItem?.isRefined,
          value: subjectOption.getValue(),
          highlighted: subjectOption.getValue()
        })
      }
      return newItems
    },
    transformCategoryItems(items, { results }) {
      let newItems = []
      for (let categoryOption of this.categoryOptionsForSelectedSubject) {
        // Filter out Orientation & Mobility category when feature flag is disabled
        if (categoryOption.getLabel() === 'Orientation & Mobility' && !this.orientationMobilityVisibility) {
          continue
        }

        const foundItem = items.find(item => item.value === categoryOption.getValue())
        if (foundItem) {
          newItems.push(foundItem)
        } else {
          newItems.push({
            count: 0,
            highlighted: categoryOption.getValue(),
            isRefined: items.find((item) => item.value === categoryOption.getValue())?.isRefined,
            label: categoryOption.getLabel(),
            value: categoryOption.getValue()
          })
        }
      }
      return newItems
    },
    transformGrades(items, {results}) {
      let existingItems = items.map((item) => {
        return {
          ...item,
          label: this.cleanGradeLabel(item.label),
        }
      });

      // Use gradeCountsBySubject to determine which grades to show
      // This shows all grades available for the subject (not just current filtered results)
      const useSubjectGrades = Object.keys(this.gradeCountsBySubject).length > 0

      if (useSubjectGrades) {
        // Show all grades that exist for this subject, in the correct order
        let newItems = []
        // Iterate through defaultGrades to maintain proper order
        this.defaultGrades.forEach((grade) => {
          // Only include grades that exist for this subject
          if (this.gradeCountsBySubject.hasOwnProperty(grade)) {
            const existingItem = existingItems.find(item => item.value === grade)
            if (existingItem) {
              newItems.push(existingItem)
            } else {
              // Grade exists for subject but not in current filtered results
              newItems.push({
                count: 0,
                highlighted: grade,
                isRefined: false,
                label: this.cleanGradeLabel(grade),
                value: grade
              })
            }
          }
        })
        return newItems
      } else {
        // Fallback to showing grades from current results
        return existingItems
      }
    },
    transformTypeItems(items, {results}) {
      let newItems = []
      for (let typeItem of ['Baseline', 'Progress Monitoring']) {
        const foundItem = items.find(item => item.value === typeItem)
        if (foundItem) {
          newItems.push(foundItem)
        } else {
          newItems.push({
            count: 0,
            highlighted: typeItem,
            isRefined: items.find((item) => item.value === typeItem)?.isRefined,
            label: typeItem,
            value: typeItem
          })
        }
      }
      return newItems
    },
    transformLanguageItems(items, { results }) {
      let newItems = []
      // Check if Baseline is selected as the assessment type
      const isBaselineSelected = this.selectedAssessmentType === 'Baseline'

      for (let lang of AVAILABLE_LANGUAGES) {
        const foundItem = items.find(item => item.value === lang)
        const isDisabled = lang === 'Spanish' && isBaselineSelected

        if (foundItem) {
          newItems.push({
            ...foundItem,
            disabled: isDisabled
          })
        } else {
          newItems.push({
            count: 0,
            highlighted: lang,
            isRefined: false,
            label: lang,
            value: lang,
            disabled: isDisabled
          })
        }
      }

      return newItems
    },
    cleanGradeLabel(grade) {
      let label;
      if (grade === 'Pre-K' || grade === 'K' || grade === 'Birth-3') {
        label = grade
      } else if (grade === 'Adult-Transition') {
        label = 'Adult Transition'
      } else {
        label = parseInt(grade)
      }
      return label
    }
  },
  computed: {
    ...mapGetters({
      isPremiumUser: 'user/isPremium',
      orientationMobilityVisibility: 'visibility/orientationMobilityVisibility',
      hasAgenticResults: 'agenticSearch/hasAssessmentResults'
    }),
    subjectFilterText() { return this.selectedSubject || 'Select a subject' },
    languageFilterText() {
      if (!this.selectedLanguage) return 'English'
      return this.selectedLanguage.charAt(0).toUpperCase() + this.selectedLanguage.slice(1)
    },
    categoryAttribute() { return this.isBehaviorSelected == true ? 'behavior_category': 'domains' },
    defaultGrades() {
      return assessmentGradesSet
    },
    isSubjectSelected() { return !!this.selectedSubject },
    isBehaviorSelected() { return this.selectedSubject === 'Behavior'},
    categoryOptionsForSelectedSubject() {
      return this.assessmentSubjectCategoriesForProfile(this.selectedSubject)
    },
    sortByItems() {
      if (this.keywordQuery) {
        return [{value: this.algoliaIndex, label: 'relevancy'}]
      } else {
        return [{value: this.algoliaIndex+'_sort_key_asc', label: 'sort_key'}]
      }
    }
  }
}

</script>
<style lang="less" scoped>
@import '~@/variables.less';
.well {
  padding: 0;
}
.ais-Panel {
  margin: 1rem;
}

.filter-header-with-tooltip {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);

  .filter-header {
    margin: 0;
  }

  .info-icon {
    font-size: 20px;
    color: @gray;
    cursor: pointer;
    user-select: none;

    &:hover {
      color: @gray-dark;
    }

    &:focus {
      outline: 2px solid var(--blue-default);
      outline-offset: 2px;
      border-radius: 50%;
    }

    &:focus:not(:focus-visible) {
      outline: none;
    }

    &:focus-visible {
      outline: 2px solid var(--blue-default);
      outline-offset: 2px;
      border-radius: 50%;
    }
  }
}
::v-deep {
  .disabled {
    cursor: not-allowed;
    color: @gray-light;
    font-style: italic;
    font-weight: normal;
  }
  .ais-RefinementList-labelText {
    padding: 0px 5px;
    margin: 0.1rem;
  }
  .ais-RefinementList-item {
    display: block;
    cursor: pointer;
    .ais-RefinementList-label {
      border-radius: 10px;
      display: block;
    }
    &:hover .ais-RefinementList-label:not(.selected):not(.disabled) {
      background: @gray-lighter;
    }
  }
  .ais-RefinementListGrades {
    .ais-RefinementList-list {
      display: flex;
      flex-flow: wrap;
      gap: 0.25rem;
    }
    .ais-RefinementList-item {
      display: flex;
      justify-content: center;
      .disabled {
        input {
          visibility: hidden;
        }
        .ais-RefinementList-labelText {
          cursor: not-allowed;
          color: @gray-light;
        }
      }
    }
    .ais-RefinementList-label {
      flex-direction: column;
      flex-wrap: wrap;
      padding: 0;
      border-radius: 0;
      &.grades{
        display: flex;
        align-items: center;
        width: 32px;
        border-radius: var(--spacing-md);
        flex-direction: column;
        text-align: center;

        &.adult-transition {
          width: 70px;
        }

        &.birth-3 {
          .ais-RefinementList-labelText {
            padding: 0;
            margin: var(--spacing-xs) 0;
          }
        }

        input {
          display: block;
          margin: 2px auto;
        }

        .lockable-input-wrapper {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 1.25rem;
        }

        .ais-RefinementList-labelText {
          line-height: 1.5;
          margin-top: var(--spacing-xs);
          margin-bottom: 0;
          font-size: var(--text-md);
          word-wrap: break-word;
          overflow-wrap: break-word;
          white-space: normal;
          text-overflow: clip;
          text-align: center;
        }
      }
    }
  }
  .ais-RefinementList-checkbox {
    box-shadow: none;
    border-color: var(--gray-weak);
  }
  .ais-RefinementList-labelText {
    cursor: pointer;
    font-weight: normal;
    color: #245682;
  }
  .ais-RefinementList-labelText {
    font-weight: normal;
    color: #245682;
    margin-bottom: -2px;
  }
  .selected:not(.disabled) {
    background-color: @gb_blue_medium;
    .ais-RefinementList-labelText {
      color: #FFF;
    }
    .ais-RefinementList-checkbox {
      appearance: auto;
    }
  }
  .ais-Menu ul {
    list-style: none;
    padding-left: 0;
    .ais-RefinementList-label {
      padding: 5px;
      border-radius: 10px;
      &:hover:not(.selected) {
        background-color: @gray-lighter
      }
    }
    .ais-RefinementList-labelText.disabled {
      margin-left: .8rem;
      cursor: not-allowed;
      color: @gray-light;
      font-style: italic;
      font-weight: normal
    }
  }
  .ais-SearchBox {
    padding-bottom: 20px;
  }
  .ais-RefinementList-count {
    display: none;
  }

  .browse-assessments-subject-dropdown,
  .browse-assessments-language-dropdown {
    width: 100%;
    .dropdown-menu li {
      cursor: pointer;
      a:hover {
        background-color: var(--blue-default);
        color: white;
      }
    }
    a.dropdown-toggle {
      width: 100%;
      text-align: left;
      display: flex;
      align-items: center;
      border-color: @gray-light;
      &:hover {
        border-color: var(--gray-strong);
      }
    }
    .fa {
      margin-left: auto
    }
  }
  .lockable-input-wrapper .lockable-input .input {
    display: flex;
  }
}
</style>